import { CsvExportButton } from 'new-components/DLS/OpTableCore/components';
import { UseOpQueryOptions } from 'utils/customHooks/useOpQuery';
import {
  OpTableColumn,
  OpTableRecordType,
  TableState,
} from 'new-components/DLS/OpTableCore/types';
import { ensurePayloadAndQuery } from 'utils/ensurePayloadAndQuery';
import { useFetchAllViaCursor } from 'utils/customHooks/useFetchAllViaCursor';
import { HeliumRawClient } from '@msi-alta/helium-client';
import { useMemo } from 'react';
import { createQueryStringParamsFromTableState } from '../createQueryStringParamsFromTableState';
import { GetterApiEndpointNames } from '../OpDataFetchTable';

type HeliumClientResponse<T extends GetterApiEndpointNames> = Awaited<
  ReturnType<HeliumRawClient[T]>
>;
type HeliumClientResponseData<T extends GetterApiEndpointNames> =
  HeliumClientResponse<T>['json']['data'];

interface CursorDataFetchCsvExportButtonProps<
  T extends GetterApiEndpointNames,
  Selection,
  RecordType extends OpTableRecordType,
> {
  opQueryProps: UseOpQueryOptions<T, Selection>;
  debouncedTableState: TableState<RecordType>;
  columnsFilterCallback?: (column: OpTableColumn<RecordType>) => boolean;
  filename?: string;
}

// Specific to cursor based data fetch tables
export const CursorDataFetchCsvExportButton = <
  T extends GetterApiEndpointNames,
  Selection,
  RecordType extends OpTableRecordType,
>({
  opQueryProps,
  debouncedTableState,
  columnsFilterCallback,
  filename,
}: CursorDataFetchCsvExportButtonProps<T, Selection, RecordType>) => {
  // useMemo ensures that the array reference remains stable unless its dependencies actually change, preventing unnecessary re-renders and cache misses in TanStack Query
  const { parametersArray, withQuery } = useMemo(
    () =>
      ensurePayloadAndQuery(
        opQueryProps.apiEndpointName,
        opQueryProps.parameters,
      ),
    [opQueryProps.apiEndpointName, opQueryProps.parameters],
  );

  // Find the index of the queries (last object)
  const queriesIndex = parametersArray.findIndex(
    (param?: number | string | {} | null) => typeof param === 'object',
  );

  if (withQuery && queriesIndex === -1) {
    // ensurePayloadAndQuery guarantees the presence of a queries object, so throw since something is way off...
    throw new Error(
      `Critical: ensurePayloadAndQuery did not ensure a query object for API endpoint ${opQueryProps.apiEndpointName}`,
    );
  }

  if (withQuery) {
    // Add queryStringParams to their correct spot in parameters array
    parametersArray[queriesIndex] = {
      ...(parametersArray[queriesIndex] as Record<string, any>),
      ...createQueryStringParamsFromTableState({
        tableState: debouncedTableState,
        isUsingCursor: true,
      }),
    };
  }

  const { data, isFetching, hasNextPage, refetch } = useFetchAllViaCursor({
    apiEndpointName: opQueryProps.apiEndpointName,
    parameters: parametersArray,
    enabled: false,
  });

  // Can filter columns with createColumns
  const computedColumns = debouncedTableState.columns.filter(
    columnsFilterCallback || (() => true),
  ) as OpTableColumn<HeliumClientResponseData<T>>[];

  const exportedData =
    data?.pages.reduce<HeliumClientResponseData<T>>((prev, next) => {
      const transformed = (next.json.data || []).map((record) => {
        let newRecord = record;
        computedColumns.forEach(({ key, createCsvExportCellValue }) => {
          if (createCsvExportCellValue) {
            if (newRecord === record) newRecord = { ...record };
            (newRecord as Record<string, any>)[String(key)] =
              createCsvExportCellValue(record as HeliumClientResponseData<T>);
          }
        });
        return newRecord;
      });
      return [...prev, ...transformed];
    }, []) || [];

  // Manually fetches each query on export button click
  const onExportClick = () => {
    refetch();
  };
  return (
    <CsvExportButton
      /**
       * As there is a small delay between the last page being
       * fetched and the refetch we want to make sure we consider
       * the data loading if there are more pages to fetch
       */
      isLoading={isFetching || hasNextPage}
      onClick={onExportClick}
      data={exportedData}
      columns={computedColumns}
      filename={filename}
    />
  );
};
